<script setup lang="ts">
import DashboardMenuItemsContainer from './DashboardMenuItemsContainer.vue'
import { useDashboardStore, useDashboardUndoService, showToast } from '@tenorlab/vue-dashboard'
import { blankDashboardConfig, useDashboardStorageService } from '@tenorlab/vue-dashboard/core'
import { sortDashboardConfigs, getNewDashboardId } from '@/utils/'
import { getDashboardDefaults } from '@/dashboard-defaults'
import { useNavigationStore } from '@/state/'
import { useConfig } from '@/config/'
import type { IDashboardConfig } from '@tenorlab/vue-dashboard'
import type { INavigationItem } from '@/models'
import type { TMenuItemContainerProps } from '../menu-items-container/models'

defineProps<TMenuItemContainerProps>()

const emits = defineEmits<{
  (e: 'expandOrCollapse', params: INavigationItem): any
  (e: 'menuItemClicked', params: INavigationItem): any
}>()

const config = useConfig()
const clientAppKey = config.global.clientAppKey
const user = { id: 1234 }
const userId = user.id
const dashboardStore = useDashboardStore()
const dashboardStorageService = useDashboardStorageService()
const undoService = useDashboardUndoService()
const navigationStore = useNavigationStore()

const {
  // isLoading, isEditing,
  currentDashboardConfig,
} = dashboardStore.computed
const getAllDashboardConfigs = () => sortDashboardConfigs(dashboardStore.computed.allDashboardConfigs.value)
const getCurrentDashboardId = () => currentDashboardConfig.value.dashboardId
const getDefaults = async () => {
  return await getDashboardDefaults(user, clientAppKey)
}

const saveDashboardConfigs = async (updatedConfigs: IDashboardConfig[], deletedDashboardId?: string) => {
  const defaults = await getDefaults()
  const saveResult = await dashboardStorageService.saveDashboards(
    userId,
    clientAppKey,
    updatedConfigs,
    defaults.widgetsCatalog,
  )
  if (!saveResult) {
    showToast({
      children: 'Error saving dashboards.',
      severity: 'error',
    })
  } else {
    if (deletedDashboardId) {
      // Case 1: Dashboard was deleted. Remove its history.
      undoService.removeUndoHistoryForDashboard(deletedDashboardId) // Use the dedicated removal function
    } else {
      // Case 2: Standard save/update. Record the successful final state.

      // 1. Find the newly updated configuration within the array
      const finalConfig = updatedConfigs.find((config) => config.dashboardId === getCurrentDashboardId())

      // 1. Add entry to undo history (Only for standard config changes, not deletion)
      // NOTE: In the deletion scenario (where deletedDashboardId is present),
      // you skip adding an undo entry because the goal is to delete, not record a change.
      if (finalConfig) {
        undoService.addUndoEntry(finalConfig)
      }
    }
  }
}

const onAddClicked = async () => {
  let name = window.prompt('Enter a name for the new dashboard')
  if (name) {
    dashboardStore.setIsLoading(true)
    const currentConfigs = [...getAllDashboardConfigs()]
    let newId = getNewDashboardId(currentConfigs)
    // const defaults = await getDefaults()
    // const defaultConfig = defaults.dashboardConfig
    const defaultConfig = {
      ...blankDashboardConfig,
      userId: userId,
      clientAppKey: clientAppKey,
    }
    const item: IDashboardConfig = {
      ...defaultConfig,
      dashboardId: `${newId}`,
      dashboardName: name || `Dashboard ${newId}`,
    }
    const updatedConfigs = sortDashboardConfigs(dashboardStore.addDashboardConfig(item))
    await saveDashboardConfigs(updatedConfigs)
    dashboardStore.setCurrentDashboardConfig(updatedConfigs.find((x) => x.dashboardId === item.dashboardId)!)
    await navigationStore.selectMenuItemById(item.dashboardId, updatedConfigs)
    setTimeout(() => dashboardStore.setIsLoading(false), 250)
    await navigateTo({ path: `/dashboards/${item.dashboardId}` })
  }
}

const onRenameClicked = async (navigationItem: INavigationItem) => {
  let newName = window.prompt('Enter a new name for this dashboard', navigationItem.name)
  if (newName) {
    dashboardStore.setIsLoading(true)
    const dashboardId: any = navigationItem.id
    const allConfigs = [...getAllDashboardConfigs()]
    const configIndex = allConfigs.findIndex((x) => x.dashboardId === dashboardId)
    const updatedConfig = allConfigs[configIndex]!
    updatedConfig.dashboardName = newName

    const updatedConfigs = sortDashboardConfigs(allConfigs)
    await saveDashboardConfigs(updatedConfigs)
    dashboardStore.setCurrentDashboardConfig(updatedConfig!)
    await navigationStore.selectMenuItemById(dashboardId, updatedConfigs)
    setTimeout(() => dashboardStore.setIsLoading(false), 250)
  }
}

const onDeleteClicked = async (navigationItem: INavigationItem) => {
  if (window.confirm('Are you sure you want to delete this dashboard?')) {
    dashboardStore.setIsLoading(true)
    const id = navigationItem.id
    const updatedConfigs = sortDashboardConfigs(dashboardStore.deleteDashboardConfigById(id))
    await saveDashboardConfigs(updatedConfigs, id)
    // select the default dashboard
    const configToSelect = updatedConfigs.find((x) => x.dashboardId === 'default')!
    // select the previous existing dashboard
    dashboardStore.setCurrentDashboardConfig(configToSelect)
    await navigationStore.selectMenuItemById(configToSelect.dashboardId, updatedConfigs)
    setTimeout(() => dashboardStore.setIsLoading(false), 250)
  }
}

const onExpandOrCollapse = (value: INavigationItem) => {
  emits('expandOrCollapse', value)
}
const onMenuItemClicked = (value: INavigationItem) => {
  emits('menuItemClicked', value)
}
</script>
<template>
  <DashboardMenuItemsContainer
    :key="model.name"
    :model="model"
    :isCollapsed="isCollapsed"
    @expandOrCollapse="onExpandOrCollapse"
    @menuItemClicked="onMenuItemClicked"
    @renameClick="onRenameClicked"
    @deleteClick="onDeleteClicked"
    @addClick="onAddClicked"
  />
</template>
